/**
 * Response shape from /app/v1/calls/ (CallViewSet, OpenSearch-backed).
 *
 * Nested objects within `from` and `to` are nullable — a call may not have
 * an extension, phone number, location, or user associated with it.
 */

interface CallEndpoint {
  caller_id: string | null
  formatted: string | null
  cnam_location: string | null
  extension: { id: number; name: string; number: string } | null
  phone_number: { id: number; name: string; number: string } | null
  location: { id: number; name: string } | null
  user: { id: number; name: string } | null
}

export interface CallEntry {
  id: number
  company: { id: number; name: string } | null
  pbx: { id: number; name: string } | null
  status: string
  type: string
  direction: 'inbound' | 'outbound' | 'internal'
  from: CallEndpoint | null
  to: CallEndpoint | null
  is_missed: boolean
  duration_in_seconds: number
  started_at: string
  ended_at: string | null
  externally_transferred_at: string | null
  voicemail_id: number | null
  fax_id: number | null
  transcript_id: number | null
  transcription_status: string | null
  call_sentiment: {
    customer_overall_score: string | null
    office_overall_score: string | null
  } | null
  created_at: string
  updated_at: string
}

/**
 * Mapped type used by IncomingCallsTable.
 * Fields are derived from CallEntry for display purposes.
 */
export interface CallHistoryEntry {
  id: number
  source_name: string
  src_ext: string
  mango_source_number: string
  call_time: string
  dst_ext: string
}

/**
 * Mapped type used by EmergencyCallsTable.
 * Fields are derived from CallEntry for display purposes.
 */
export interface EmergencyCall {
  id: number
  src_ext: string
  call_time: string
  billable_duration: number
  destination_number: string
}

/** Maps a raw CallEntry from /app/v1/calls/ to the display-friendly CallHistoryEntry. */
export function mapCallEntryToHistoryEntry(call: CallEntry): CallHistoryEntry {
  return {
    id: call.id,
    source_name:
      call.from?.caller_id ||
      call.from?.user?.name ||
      call.from?.extension?.name ||
      '',
    src_ext: call.from?.extension?.number || '',
    mango_source_number: call.from?.formatted || '',
    call_time: call.started_at,
    dst_ext: call.to?.extension?.number || call.to?.phone_number?.number || '',
  }
}
